import { useMemo, useState } from 'react';
import {
  fieldRoleSchema,
  fieldTypeSchema,
  slugify,
  type FieldDescriptor,
  type FieldRole,
  type FieldType,
  type KindDef,
} from '@gm-binder/shared';
import { Button, Field, Input, Select, IconButton } from '../ui';
import { LIST_VIEW_OPTIONS, DETAIL_VIEW_OPTIONS } from '../../lib/viewRegistry';
import styles from './KindBuilder.module.css';

const TYPE_LABELS: Record<FieldType, string> = {
  text: 'Testo',
  markdown: 'Markdown',
  number: 'Numero',
  boolean: 'Sì / No',
  select: 'Scelta',
  tags: 'Etichette',
  image: 'Immagine',
  ref: 'Riferimento',
  refList: 'Riferimenti',
  refCount: 'Riferimenti (con quantità)',
  statblock: 'Statistiche',
  rows: 'Righe',
};

const ROLE_LABELS: Record<FieldRole, string> = {
  title: 'Titolo',
  subtitle: 'Sottotitolo',
  icon: 'Icona',
  image: 'Immagine',
  body: 'Corpo',
};

interface DraftField extends FieldDescriptor {
  _id: number;
}

export interface KindBuilderProps {
  existingKindIds: string[];
  /** Kinds available as ref/refList targets (built-in + custom). */
  kindOptions: { id: string; label: string }[];
  onCreate: (kind: KindDef) => void;
  onCancel: () => void;
}

let fieldSeq = 1;

function blankField(): DraftField {
  return { _id: fieldSeq++, key: '', type: 'text', label: '' };
}

/** Custom-kind builder: define fields + pick list/detail visualization → writes a KindDef. */
export function KindBuilder({ existingKindIds, kindOptions, onCreate, onCancel }: KindBuilderProps) {
  const [label, setLabel] = useState('');
  const [icon, setIcon] = useState('📄');
  const [scope, setScope] = useState<'adventure' | 'system'>('adventure');
  const [listView, setListView] = useState('cards');
  const [detailView, setDetailView] = useState('profile');
  const [fields, setFields] = useState<DraftField[]>([
    { _id: fieldSeq++, key: 'name', type: 'text', label: 'Nome', role: 'title' },
    { _id: fieldSeq++, key: 'body', type: 'markdown', label: 'Descrizione', role: 'body' },
  ]);

  const id = useMemo(() => slugify(label), [label]);
  const idTaken = id !== '' && existingKindIds.includes(id);
  const valid = label.trim() !== '' && !idTaken && fields.every((f) => f.label.trim() !== '');

  const setField = (fieldId: number, patch: Partial<DraftField>) =>
    setFields((prev) => prev.map((f) => (f._id === fieldId ? { ...f, ...patch } : f)));

  const submit = () => {
    if (!valid) return;
    const cleaned: FieldDescriptor[] = fields.map((f) => {
      const key = f.key.trim() || slugify(f.label);
      const out: FieldDescriptor = { key, type: f.type, label: f.label.trim() };
      if (f.role) out.role = f.role;
      if ((f.type === 'ref' || f.type === 'refList') && f.target) out.target = f.target;
      if (f.type === 'select' && Array.isArray(f.options) && f.options.length) out.options = f.options;
      return out;
    });
    // Guarantee a title role so generic views always have a heading.
    if (!cleaned.some((f) => f.role === 'title') && cleaned[0]) cleaned[0].role = 'title';

    const kind: KindDef = {
      id,
      label: label.trim(),
      icon: icon || '📄',
      scope,
      fields: cleaned,
      views: {
        list: listView as KindDef['views']['list'],
        detail: detailView as KindDef['views']['detail'],
      },
      storageDir: id,
    };
    onCreate(kind);
  };

  return (
    <div className={styles.builder}>
      <div className={styles.row}>
        <Field label="Nome del tipo" error={idTaken ? 'Esiste già un tipo con questo id' : undefined}>
          <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="es. Fazioni" />
        </Field>
        <Field label="Icona" className={styles.iconField}>
          <Input value={icon} onChange={(e) => setIcon(e.target.value)} maxLength={4} />
        </Field>
        <Field label="Ambito">
          <Select value={scope} onChange={(e) => setScope(e.target.value as 'adventure' | 'system')}>
            <option value="adventure">Avventura</option>
            <option value="system">Sistema</option>
          </Select>
        </Field>
      </div>

      {id && <p className={styles.hint}>id tipo: <code>{id}</code></p>}

      <div className={styles.fields}>
        <div className={styles.fieldsHead}>
          <span>Campi</span>
          <Button size="sm" icon="＋" onClick={() => setFields((p) => [...p, blankField()])}>
            Aggiungi campo
          </Button>
        </div>
        {fields.map((f) => (
          <div key={f._id} className={styles.fieldRow}>
            <Input
              className={styles.fLabel}
              value={f.label}
              placeholder="Etichetta"
              onChange={(e) => setField(f._id, { label: e.target.value, key: f.key || slugify(e.target.value) })}
            />
            <Input
              className={styles.fKey}
              value={f.key}
              placeholder="chiave"
              onChange={(e) => setField(f._id, { key: e.target.value })}
            />
            <Select
              className={styles.fType}
              value={f.type}
              onChange={(e) => setField(f._id, { type: e.target.value as FieldType })}
            >
              {fieldTypeSchema.options.map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABELS[t]}
                </option>
              ))}
            </Select>
            <Select
              className={styles.fRole}
              value={f.role ?? ''}
              onChange={(e) => setField(f._id, { role: (e.target.value || undefined) as FieldRole | undefined })}
            >
              <option value="">(nessun ruolo)</option>
              {fieldRoleSchema.options.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </Select>
            {(f.type === 'ref' || f.type === 'refList') && (
              <Select
                className={styles.fTarget}
                value={typeof f.target === 'string' ? f.target : ''}
                onChange={(e) => setField(f._id, { target: e.target.value || undefined })}
              >
                <option value="">(qualsiasi)</option>
                {kindOptions.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.label}
                  </option>
                ))}
              </Select>
            )}
            {f.type === 'select' && (
              <Input
                className={styles.fTarget}
                value={Array.isArray(f.options) ? f.options.join(', ') : ''}
                placeholder="opzioni, separate, da virgole"
                onChange={(e) =>
                  setField(f._id, {
                    options: e.target.value
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
              />
            )}
            <IconButton
              label="Rimuovi campo"
              onClick={() => setFields((p) => p.filter((x) => x._id !== f._id))}
            >
              🗑️
            </IconButton>
          </div>
        ))}
      </div>

      <div className={styles.row}>
        <Field label="Vista elenco">
          <Select value={listView} onChange={(e) => setListView(e.target.value)}>
            {LIST_VIEW_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Vista dettaglio">
          <Select value={detailView} onChange={(e) => setDetailView(e.target.value)}>
            {DETAIL_VIEW_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <div className={styles.actions}>
        <Button variant="ghost" onClick={onCancel}>
          Annulla
        </Button>
        <Button variant="primary" disabled={!valid} onClick={submit}>
          Crea tipo
        </Button>
      </div>
    </div>
  );
}
